"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { landingCopy } from "./copy";
import { motion } from "framer-motion";
import EnquiryForm from "./EnquiryForm";
import { useEnquiryForm } from "./useEnquiryForm";
import { LandingSectionBadge } from "./LandingSectionBadge";

export default function CTABand() {
  const { isAuthenticated } = useAuth();
  const { isOpen, setIsOpen } = useEnquiryForm();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.42, 0, 0.58, 1] as const, // easeOut cubic bezier
      },
    },
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary via-blue-600 to-cyan-600 py-16 text-primary-foreground lg:py-24">
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
        <div className="absolute left-10 top-10 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute bottom-10 right-10 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/5 blur-2xl" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="mx-auto max-w-3xl text-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.div variants={itemVariants} className="flex justify-center">
            <LandingSectionBadge variant="onGradient" className="mb-6">
              Next steps
            </LandingSectionBadge>
          </motion.div>
          <motion.h2
            className="mb-8 text-3xl font-bold leading-relaxed sm:text-4xl lg:text-5xl"
            variants={itemVariants}
          >
            Ready to transform your debt recovery?
          </motion.h2>
          <motion.p
            className="text-lg text-primary-foreground/90 mb-8 max-w-2xl mx-auto"
            variants={itemVariants}
          >
            Join leading financial institutions using GrowFinx to automate recovery
            workflows and maximize collection efficiency.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            variants={itemVariants}
          >
            <div className="transition-transform hover:scale-[1.02] active:scale-[0.98]">
              {isAuthenticated ? (
                <Button
                  asChild
                  size="lg"
                  variant="white"
                  className="text-base px-8 py-6 h-auto"
                >
                  <Link href="/dashboard">
                    {landingCopy.cta.dashboard}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              ) : (
                <Button
                  size="lg"
                  variant="white"
                  className="text-base px-8 py-6 h-auto"
                  onClick={() => setIsOpen(true)}
                >
                  {landingCopy.cta.primary}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              )}
            </div>
            <div className="transition-transform hover:scale-[1.02] active:scale-[0.98]">
              <Button
                asChild
                size="lg"
                variant="outline"
                className="text-base px-8 py-6 h-auto border-2 border-primary-foreground/40 text-primary-foreground bg-transparent hover:bg-primary-foreground/10 hover:border-primary-foreground/60"
              >
                <Link href="#">
                  {landingCopy.cta.demo}
                  <Play className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
      <EnquiryForm open={isOpen} onOpenChange={setIsOpen} />
    </section>
  );
}

